import maplibregl from "maplibre-gl";
import { escapeHtml } from "../escape";
import { $airport, $airportPanelVisible, $theme, $toggles, $weather } from "../store";
import type { Airport, Theme, Weather } from "../types";

const MINI_MAP_STYLE: Record<Theme, string> = {
  dark: "https://tiles.openfreemap.org/styles/dark",
  light: "https://tiles.openfreemap.org/styles/positron",
};

/** Visibility display. Null = no METAR reading; ≥10 km is rendered as
 *  "10+ km" since aviation visibility above 10 km is effectively unlimited
 *  and METAR sources cap reporting at that. */
export function formatVis(visKm: number | null): string {
  if (visKm == null) return "—";
  if (visKm >= 10) return "10+ km";
  return `${visKm.toFixed(1)} km`;
}

/** Map METAR flight category (or fallback) to a Phosphor icon class. */
function conditionIconClass(cond: string | undefined | null): string {
  switch ((cond ?? "").toUpperCase()) {
    case "VFR":
      return "ph ph-sun";
    case "MVFR":
      return "ph ph-cloud-sun";
    case "IFR":
      return "ph ph-cloud";
    case "LIFR":
      return "ph ph-cloud-fog";
    default:
      return "ph ph-cloud-sun";
  }
}

/** Build the runway-card title like "16L · 16R · 22". Falls back to the
 *  single `activeEnd` (older API revisions) or to "—". */
function formatActiveEnds(ap: Airport): string {
  if (ap.activeEnds && ap.activeEnds.length > 0) {
    return ap.activeEnds.map((e) => e.inUse).join(" · ");
  }
  return ap.activeEnd ?? ap.activeArr ?? "—";
}

export function renderAirportPanel(host: HTMLElement) {
  host.innerHTML = `
    <div class="ap-toggles">
      <button class="chip" data-k="weather" title="Toggle weather"><i class="ph ph-cloud-sun"></i><span>Wx</span></button>
      <button class="chip" data-k="runways" title="Toggle runways"><i class="ph ph-road-horizon"></i><span>Rwy</span></button>
      <button class="chip" data-k="wind"    title="Toggle wind"   ><i class="ph ph-wind"></i><span>Wind</span></button>
    </div>
    <div class="ap-scroll">
      <div class="card" id="wxCard"></div>
      <div class="card" id="rwCard"></div>
      <div class="card" id="wcCard"></div>
    </div>
  `;

  $airportPanelVisible.subscribe((v) => host.classList.toggle("hidden", !v));

  const wx = host.querySelector("#wxCard") as HTMLElement;
  const rw = host.querySelector("#rwCard") as HTMLElement;
  const wc = host.querySelector("#wcCard") as HTMLElement;

  // Toggle chips
  function syncChips() {
    const t = $toggles.get();
    host.querySelectorAll<HTMLElement>(".chip").forEach((el) => {
      const k = el.dataset.k as keyof typeof t;
      el.classList.toggle("on", !!t[k]);
    });
    wx.style.display = t.weather ? "" : "none";
    rw.style.display = t.runways ? "" : "none";
    wc.style.display = t.wind ? "" : "none";
  }
  host.querySelectorAll<HTMLElement>(".chip").forEach((el) => {
    el.addEventListener("click", () => {
      const k = el.dataset.k as "weather" | "runways" | "wind";
      $toggles.set({ ...$toggles.get(), [k]: !$toggles.get()[k] });
    });
  });
  $toggles.subscribe(syncChips);
  syncChips();

  function renderWx(w: Weather | null, ap: Airport | null) {
    if (!w || !ap) {
      wx.innerHTML = `<div class="ap-head"><div class="l"><i class="ph ph-tower-control"></i> —</div></div>`;
      return;
    }
    // ap.iata/icao, w.observedAt/condition/clouds are upstream airport/METAR
    // strings — escape them. Numeric fields are coerced and need no escaping.
    wx.innerHTML = `
      <div class="ap-head">
        <div class="l">
          <i class="ph ph-tower-control"></i>
          ${escapeHtml(ap.iata)}<span class="code-tag">${escapeHtml(ap.icao)}</span>
        </div>
        <span class="right-meta">METAR · ${escapeHtml(w.observedAt ?? "")}</span>
      </div>
      <div class="divider"></div>
      <div class="wx">
        <div class="big">
          <span class="t">${Math.round(w.tempC)}</span>
          <span class="unit">°C</span>
          <span class="cond"><i class="${conditionIconClass(w.condition)}"></i>${escapeHtml(w.condition)}</span>
        </div>
        <div class="grid">
          <div class="item"><i class="ph ph-wind"></i><div><div class="k">Wind</div><div class="v">${w.windKt} kt · ${w.windDeg}°</div></div></div>
          <div class="item"><i class="ph ph-eye"></i><div><div class="k">Visibility</div><div class="v">${formatVis(w.visKm)}</div></div></div>
          <div class="item"><i class="ph ph-cloud"></i><div><div class="k">Clouds</div><div class="v">${escapeHtml(w.clouds)}</div></div></div>
          <div class="item"><i class="ph ph-drop"></i><div><div class="k">Humidity</div><div class="v">${w.humidity}%</div></div></div>
          <div class="item"><i class="ph ph-thermometer-simple"></i><div><div class="k">Dewpoint</div><div class="v">${w.dewC}°C</div></div></div>
          <div class="item"><i class="ph ph-gauge"></i><div><div class="k">QNH</div><div class="v">${w.qnh} hPa</div></div></div>
        </div>
      </div>
    `;
  }

  // Track the active mini-map instance so we can dispose it before
  // re-rendering (each render rebuilds .diagram, leaving an orphan
  // WebGL canvas otherwise).
  let miniMap: maplibregl.Map | null = null;

  function renderRunways(ap: Airport | null, w: Weather | null) {
    if (miniMap) {
      miniMap.remove();
      miniMap = null;
    }
    if (!ap) {
      rw.innerHTML = `<div class="ap-head"><div class="l"><i class="ph ph-road-horizon"></i> Runways</div></div>`;
      return;
    }
    const svg = runwaySvg(ap, w);
    rw.innerHTML = `
      <div class="ap-head">
        <div class="l"><i class="ph ph-road-horizon"></i> Runways</div>
        <span class="right-meta">${escapeHtml(ap.icao)}</span>
      </div>
      <div class="divider"></div>
      <div class="rw">
        <div class="top">
          <span class="title">Active: ${escapeHtml(formatActiveEnds(ap))}</span>
          <span class="sub">Wind ${w ? `${w.windDeg}°/${w.windKt}kt` : "—"}${ap.activeHeadwindKt != null ? ` · 向${ap.activeHeadwindKt}kt 横${ap.activeCrosswindKt ?? 0}kt` : ""}</span>
        </div>
        <div class="diagram">
          <div class="rw-mini-map"></div>
          ${svg}
        </div>
        <div class="rw-legend">
          <div class="it act"><span class="sw"></span>Active</div>
          <div class="it"><span class="sw"></span>Inactive</div>
        </div>
      </div>
    `;
    mountMiniMap(ap);
  }

  /** Spin up a tiny static MapLibre instance behind the runway SVG so
   * the user sees the airport's actual surroundings (terminals, taxiways,
   * roads) underneath the runway diagram. Interaction is fully disabled. */
  function mountMiniMap(ap: Airport) {
    const host = rw.querySelector(".rw-mini-map") as HTMLElement | null;
    if (!host) return;
    // Centre on the runways' bounding box and zoom to fit (with a little
    // padding so taxiways are visible too).
    const lats: number[] = [];
    const lons: number[] = [];
    for (const r of ap.runways) {
      lats.push(r.le_lat, r.he_lat);
      lons.push(r.le_lon, r.he_lon);
    }
    if (lats.length === 0) return;
    const bounds = new maplibregl.LngLatBounds(
      [Math.min(...lons), Math.min(...lats)],
      [Math.max(...lons), Math.max(...lats)],
    );
    miniMap = new maplibregl.Map({
      container: host,
      style: MINI_MAP_STYLE[$theme.get()],
      bounds,
      fitBoundsOptions: { padding: 28, animate: false },
      interactive: false, // no pan/zoom/rotate/click
      attributionControl: false,
    });
  }

  function renderWind(w: Weather | null) {
    if (!w) {
      wc.innerHTML = `<div class="ap-head"><div class="l"><i class="ph ph-wind"></i> Wind</div></div>`;
      return;
    }
    // Arrow points TOWARDS the wind direction (i.e. from origin compass bearing)
    const arrowDeg = (w.windDeg + 180) % 360;
    wc.innerHTML = `
      <div class="ap-head">
        <div class="l"><i class="ph ph-wind"></i> Wind</div>
        <span class="right-meta">Surface · 30 min avg</span>
      </div>
      <div class="divider"></div>
      <div class="wc">
        <div class="compass">
          <div class="face"></div>
          <span class="lbl n">N</span><span class="lbl e">E</span><span class="lbl s">S</span><span class="lbl w">W</span>
          <div class="arrow" style="transform: translate(-50%,-100%) rotate(${arrowDeg}deg)"></div>
          <div class="center"></div>
        </div>
        <div class="nums">
          <div class="big">${w.windKt}<small>kt</small></div>
          <div class="row"><span class="k">From</span><span class="v">${w.windDeg}°</span></div>
          ${w.gustKt ? `<div class="row"><span class="k">Gust</span><span class="v">${w.gustKt} kt</span></div>` : ""}
          <div class="row"><span class="k">Dew</span><span class="v">${w.dewC}°C</span></div>
        </div>
      </div>
    `;
  }

  function rerender() {
    const ap = $airport.get();
    const w = $weather.get();
    renderWx(w, ap);
    renderRunways(ap, w);
    renderWind(w);
  }

  $airport.subscribe(rerender);
  $weather.subscribe(rerender);
  // Re-render so the mini-map style follows the user's theme switch.
  $theme.subscribe(rerender);
}

/**
 * Render a proper-scale runway diagram from real lat/lon coordinates.
 * Projects each runway's two endpoints into SVG space via equirectangular
 * approximation (good enough for an airport-sized footprint).
 */
function runwaySvg(ap: Airport, w: Weather | null): string {
  const W = 280,
    H = 200,
    PAD = 22;
  // Compute bounds
  const lats: number[] = [];
  const lons: number[] = [];
  for (const r of ap.runways) {
    lats.push(r.le_lat, r.he_lat);
    lons.push(r.le_lon, r.he_lon);
  }
  const latMin = Math.min(...lats),
    latMax = Math.max(...lats);
  const lonMin = Math.min(...lons),
    lonMax = Math.max(...lons);
  const dLat = Math.max(latMax - latMin, 1e-4);
  const dLon = Math.max(lonMax - lonMin, 1e-4) * Math.cos((((latMin + latMax) / 2) * Math.PI) / 180);

  // Maintain aspect ratio
  const scale = Math.min((W - PAD * 2) / dLon, (H - PAD * 2) / dLat);
  const cx = (lonMin + lonMax) / 2;
  const cy = (latMin + latMax) / 2;

  function proj(lat: number, lon: number): [number, number] {
    const x = W / 2 + (lon - cx) * Math.cos((cy * Math.PI) / 180) * scale;
    const y = H / 2 - (lat - cy) * scale;
    return [x, y];
  }

  const lines = ap.runways
    .map((r) => {
      const [x1, y1] = proj(r.le_lat, r.le_lon);
      const [x2, y2] = proj(r.he_lat, r.he_lon);
      const active =
        ap.activeEnds?.some((e) => e.runway === r.name) ??
        (ap.activeArr === r.name || ap.activeDep === r.name);
      const color = active ? "var(--accent-amber)" : "currentColor";
      const opacity = active ? 1 : 0.55;
      return `
      <line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}"
            stroke="${color}" stroke-width="10" stroke-linecap="round" opacity="${opacity * 0.35}"/>
      <line x1="${x1.toFixed(1)}" y1="${y1.toFixed(1)}" x2="${x2.toFixed(1)}" y2="${y2.toFixed(1)}"
            stroke="${color}" stroke-width="2" stroke-dasharray="5 5" opacity="${opacity}"/>
      <text x="${x1.toFixed(1)}" y="${(y1 - 8).toFixed(1)}" font-size="10" fill="${color}" opacity="${opacity}" text-anchor="middle" font-weight="600">${escapeHtml(r.le_ident)}</text>
      <text x="${x2.toFixed(1)}" y="${(y2 + 14).toFixed(1)}" font-size="10" fill="${color}" opacity="${opacity * 0.85}" text-anchor="middle" font-weight="600">${escapeHtml(r.he_ident)}</text>
    `;
    })
    .join("");

  // Wind vector arrow in top-right of diagram
  let windArrow = "";
  if (w) {
    const rad = ((w.windDeg + 180) * Math.PI) / 180;
    const len = 28;
    const dx = Math.sin(rad) * len;
    const dy = -Math.cos(rad) * len;
    const ax = W - 30;
    const ay = 30;
    windArrow = `
      <line x1="${ax}" y1="${ay}" x2="${ax + dx}" y2="${ay + dy}" stroke="var(--accent-amber)" stroke-width="2" stroke-linecap="round"/>
      <polygon points="${ax + dx},${ay + dy} ${ax + dx - 6},${ay + dy + 4} ${ax + dx - 4},${ay + dy - 4}" fill="var(--accent-amber)"/>
      <text x="${ax - 20}" y="${ay - 8}" font-size="10" fill="var(--accent-amber)" font-weight="600">${w.windDeg}°</text>
    `;
  }

  return `<svg viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg" style="color:var(--muted)">${lines}${windArrow}</svg>`;
}
